import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Plus, Pencil, Trash2, MoreHorizontal } from 'lucide-react';
import useBoardStore from '../../../store/boardStore';
import useAuthStore from '../../../store/authStore';
import { dialog } from '../../../store/dialogStore';
import { isOrgAdmin, canManageWorkspaces } from '../../../utils/permissions';
import { WORKSPACE_ICONS, defaultColorForWorkspace } from './workspaceIcons';
import WorkspaceEditModal from './WorkspaceEditModal';
import WorkspaceCreateModal from './WorkspaceCreateModal';

const ACTIVE_KEY = (uid) => `seasymon:activeWs:${uid}`;
const RECENT_WS_KEY = (uid) => `seasymon:recentWs:${uid}`;
const RECENT_LIMIT = 5;

export function loadActiveWorkspaceId(userId) {
  if (!userId) return null;
  const v = localStorage.getItem(ACTIVE_KEY(userId));
  return v ? Number(v) : null;
}
export function saveActiveWorkspaceId(userId, id) {
  if (!userId) return;
  if (id == null) localStorage.removeItem(ACTIVE_KEY(userId));
  else localStorage.setItem(ACTIVE_KEY(userId), String(id));
}

function loadRecentWs(userId) {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(RECENT_WS_KEY(userId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function saveRecentWs(userId, ids) {
  if (!userId) return;
  try { localStorage.setItem(RECENT_WS_KEY(userId), JSON.stringify(ids.slice(0, RECENT_LIMIT))); } catch {}
}
export function pushRecentWs(userId, id) {
  const list = loadRecentWs(userId);
  const next = [id, ...list.filter((x) => x !== id)].slice(0, RECENT_LIMIT);
  saveRecentWs(userId, next);
  return next;
}

export function WsIcon({ ws, size = 24 }) {
  if (ws?.icon_url) {
    return (
      <img
        src={ws.icon_url}
        alt=""
        className="rounded object-cover shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  const color = ws?.icon_color || defaultColorForWorkspace(ws?.id);
  const IconComp = ws?.icon ? WORKSPACE_ICONS[ws.icon] : null;
  if (IconComp) {
    return (
      <div
        className="rounded flex items-center justify-center shrink-0"
        style={{ width: size, height: size, backgroundColor: color }}
      >
        <IconComp className="text-white" style={{ width: size * 0.55, height: size * 0.55 }} />
      </div>
    );
  }
  const letter = (ws?.name || '?').trim().charAt(0).toUpperCase();
  return (
    <div
      className="rounded flex items-center justify-center text-white font-semibold shrink-0"
      style={{ width: size, height: size, backgroundColor: color, fontSize: size * 0.45 }}
    >
      {letter}
    </div>
  );
}

export default function WorkspaceDropdown({ userId, activeId, onChange }) {
  const user = useAuthStore((s) => s.user);
  const { workspaces, deleteWorkspace } = useBoardStore();
  // Revisao de seguranca: criar/editar (nome, icone) area exige a capability
  // org.workspace.create — Cliente e Colaborador nao tem. Excluir continua
  // separado, so isOrgAdmin (abaixo, sem usar canManage).
  const canManage = canManageWorkspaces(user);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [editingWs, setEditingWs] = useState(null);

  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  useEffect(() => { if (open && searchRef.current) searchRef.current.focus(); }, [open]);

  const active = workspaces.find((w) => w.id === activeId) || workspaces[0];
  const recent = loadRecentWs(userId)
    .map((id) => workspaces.find((w) => w.id === id))
    .filter(Boolean);

  const filtered = search
    ? workspaces.filter((w) => w.name.toLowerCase().includes(search.toLowerCase()))
    : workspaces;

  const pick = (ws) => {
    onChange(ws.id);
    setOpen(false);
    setSearch('');
  };

  const handleDelete = async (ws) => {
    try {
      await deleteWorkspace(ws.id);
      if (ws.id === activeId) onChange(workspaces.find((w) => w.id !== ws.id)?.id || null);
    } catch (err) {
      const msg = err.message || 'Erro';
      if (/cascata|force/i.test(msg)) {
        if (await dialog.confirm('Excluir workspace', `${msg}\n\nDeseja excluir o workspace e TODOS os quadros dentro?`, { confirmLabel: 'Excluir tudo', danger: true })) {
          try {
            await deleteWorkspace(ws.id, { force: true });
            if (ws.id === activeId) onChange(workspaces.find((w) => w.id !== ws.id)?.id || null);
          } catch (e2) {
            dialog.alert('Erro', e2.message);
          }
        }
      } else {
        dialog.alert('Erro', msg);
      }
    }
  };

  return (
    <div ref={ref} className="relative px-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg bg-main border border-border hover:border-accent/40 transition-colors"
      >
        <WsIcon ws={active} size={22} />
        <span className="flex-1 text-left text-sm text-text-primary truncate font-medium">
          {active?.name || 'Selecionar área de trabalho'}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-text-secondary transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-2 right-2 mt-1 z-40 bg-card border border-border rounded-lg shadow-xl py-2 max-h-[480px] overflow-y-auto">
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary" />
              <input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Procurar uma área de trabalho"
                className="w-full bg-main border border-border rounded-lg pl-7 pr-2 py-1.5 text-xs text-text-primary placeholder-text-secondary outline-none focus:border-accent/50"
              />
            </div>
          </div>

          {!search && recent.length > 0 && (
            <>
              <div className="px-3 py-1 text-[10px] font-semibold text-text-secondary uppercase tracking-wider">
                Recentes
              </div>
              {recent.map((w) => (
                <WsRow key={`r-${w.id}`} ws={w} active={w.id === activeId} onClick={() => pick(w)} />
              ))}
              <div className="my-1 border-t border-border/50" />
            </>
          )}

          <div className="px-3 py-1 text-[10px] font-semibold text-text-secondary uppercase tracking-wider">
            {search ? 'Resultados' : 'Todas'}
          </div>
          {filtered.map((w) => (
            <WsRow
              key={`a-${w.id}`}
              ws={w}
              active={w.id === activeId}
              onClick={() => pick(w)}
              onEdit={canManage ? () => { setEditingWs(w); setOpen(false); } : null}
              onDelete={isOrgAdmin(user) ? () => handleDelete(w) : null}
            />
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-text-secondary italic">Nenhuma área encontrada</div>
          )}

          {canManage && (
            <div className="mt-2 pt-2 border-t border-border/50">
              <button
                onClick={() => { setCreatingOpen(true); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-primary hover:bg-white/5 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Adicionar área de trabalho
              </button>
            </div>
          )}
        </div>
      )}

      {editingWs && (
        <WorkspaceEditModal
          workspace={editingWs}
          onClose={() => setEditingWs(null)}
        />
      )}

      {creatingOpen && (
        <WorkspaceCreateModal
          onClose={() => setCreatingOpen(false)}
          onCreated={(ws) => {
            setCreatingOpen(false);
            onChange(ws.id);
          }}
        />
      )}
    </div>
  );
}

function WsRow({ ws, active, onClick, onEdit, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  return (
    <div className={`group/ws flex items-center gap-2 px-3 py-1.5 cursor-pointer transition-colors ${active ? 'bg-accent/10' : 'hover:bg-white/5'}`}>
      <div className="flex items-center gap-2 flex-1 min-w-0" onClick={onClick}>
        <WsIcon ws={ws} size={20} />
        <span className="text-xs text-text-primary truncate">{ws.name}</span>
      </div>
      {(onEdit || onDelete) && (
        <div className="relative opacity-0 group-hover/ws:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            className="p-0.5 rounded hover:bg-white/10 text-text-secondary hover:text-text-primary"
          >
            <MoreHorizontal className="w-3.5 h-3.5" />
          </button>
          {menuOpen && (
            <div
              ref={menuRef}
              className="absolute right-0 top-full mt-1 z-50 w-36 bg-card border border-border rounded-lg shadow-xl py-1"
            >
              {onEdit && (
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEdit(); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-primary hover:bg-white/5"
                >
                  <Pencil className="w-3 h-3" /> Editar
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete?.(); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-danger hover:bg-danger/10"
                >
                  <Trash2 className="w-3 h-3" /> Excluir
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
